/**
 * Lenkesiden (sak 232, T6) og lenkestatusen i personlista.
 *
 * LENKESIDEN HAR INGEN SESJON OG INGEN ORGANISASJON. Hver forespørsel sender derfor
 * `sendOrganizationHeader: false` — en innlogget arrangør som tester lenken sin skal ikke sende sin
 * egen organisasjon med på en anonym rute. Nøkkelen er global (`qk.shareLink`), tokenet er hele
 * spørsmålet.
 */
import {
  API_VERSION_PREFIX,
  confirmLinkItemResponseSchema,
  linkPageSchema,
  linkPersonListResponseSchema,
  type ConfirmLinkItemResponse,
  type LinkPage,
  type LinkPersonListResponse,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';
import { env } from '@/env';

export function shareLinkQueryOptions(token: string) {
  return queryOptions({
    queryKey: qk.shareLink(token),
    queryFn: ({ signal }): Promise<LinkPage> =>
      apiRequest({
        path: `/share-links/${encodeURIComponent(token)}`,
        schema: linkPageSchema,
        signal,
        sendOrganizationHeader: false,
      }),
    /* Et token som ikke er en lenke svarer 404, og flere forsøk kan ikke endre det. */
    retry: false,
    /* Hver henting er en åpning organisasjonen ser; ingen skjult ny henting ved fokus. */
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** «Bekreft mottatt». Svaret skrives rett inn i siden — en ny henting ville vært en ny åpning. */
export function useConfirmLinkItem(
  token: string,
): UseMutationResult<ConfirmLinkItemResponse, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (grantId: string) =>
      apiRequest({
        path: `/share-links/${encodeURIComponent(token)}/confirm`,
        method: 'POST',
        body: { grantId },
        schema: confirmLinkItemResponseSchema,
        sendOrganizationHeader: false,
      }),
    onSuccess: (answer) => {
      client.setQueryData<LinkPage>(qk.shareLink(token), (page) =>
        page === undefined
          ? page
          : {
              ...page,
              items: page.items.map((item) =>
                item.grantId === answer.grantId
                  ? { ...item, confirmedAt: answer.confirmedAt }
                  : item,
              ),
              /* F12, F13: dokumentene, mappene og kartene bekreftes på samme vis. */
              documents: (page.documents ?? []).map((doc) =>
                doc.grantId === answer.grantId ? { ...doc, confirmedAt: answer.confirmedAt } : doc,
              ),
              maps: (page.maps ?? []).map((map) =>
                map.grantId === answer.grantId ? { ...map, confirmedAt: answer.confirmedAt } : map,
              ),
            },
      );
    },
  });
}

/** Adressen til .ics-fila for én delt ting (232.7). En vanlig lenke: nettleseren laster den ned. */
export function linkCalendarUrl(token: string, grantId: string): string {
  return `${env.apiUrl}${API_VERSION_PREFIX}/share-links/${encodeURIComponent(token)}/items/${grantId}/calendar.ics`;
}

/** Et vedlegg på lenkesiden (F11). En vanlig lenke: nettleseren laster det ned. */
export function linkFileUrl(token: string, grantId: string, attachmentId: string): string {
  return `${env.apiUrl}${API_VERSION_PREFIX}/share-links/${encodeURIComponent(token)}/items/${grantId}/files/${attachmentId}`;
}

/** Lenkepersonene på årgangen, for ikonet og statusen i personlista. */
export function linkPeopleQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.linkPeople(orgId, editionId),
    queryFn: ({ signal }): Promise<LinkPersonListResponse> =>
      apiRequest({
        path: `/editions/${editionId}/link-people`,
        schema: linkPersonListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}
